import React, { useState } from 'react';
import { toast } from './Toast';
import { X, Sparkles, Loader } from 'lucide-react';
import { crmApi } from '../services/crmApi';

interface Props {
  applicationId: number;
  onClose: () => void;
  onGenerate: (data: CertificateFormData) => void;
}

export interface CertificateFormData {
  certificateType: 'internship' | 'volunteer' | 'employment';
  position: string;
  companyName: string;
  startDate: string;
  endDate: string;
  responsibilities: string[];
  positiveTraits: string[];
  skillsGained: string[];
  assessment: string;
}

export const CertificateFormModal: React.FC<Props> = ({ applicationId, onClose, onGenerate }) => {
  const [certificateType, setCertificateType] = useState<'internship' | 'volunteer' | 'employment'>('internship');
  const [position, setPosition] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [responsibilities, setResponsibilities] = useState<string[]>(['', '', '']);
  const [positiveTraits, setPositiveTraits] = useState<string[]>(['', '', '']);
  const [skillsGained, setSkillsGained] = useState<string[]>(['', '', '']);
  const [assessment, setAssessment] = useState('');

  const [loadingAI, setLoadingAI] = useState(false);
  const [aiSuggestionsLoaded, setAiSuggestionsLoaded] = useState(false);

  const certificateTypeLabels = {
    internship: 'Świadectwo Praktyk',
    volunteer: 'Zaświadczenie Wolontariatu',
    employment: 'Zaświadczenie Zatrudnienia',
  };

  const handleGetAISuggestions = async () => {
    if (!position || !companyName) {
      toast.info('Proszę wypełnić stanowisko i nazwę firmy/organizacji');
      return;
    }

    setLoadingAI(true);
    try {
      const suggestions = await crmApi.getCertificateAISuggestions(
        applicationId,
        certificateType,
        position,
        companyName
      );

      // Update form with AI suggestions
      setResponsibilities(suggestions.responsibilities || ['', '', '']);
      setPositiveTraits(suggestions.positiveTraits || ['', '', '']);
      setSkillsGained(suggestions.skillsGained || ['', '', '']);
      setAssessment(suggestions.assessment || '');
      setAiSuggestionsLoaded(true);
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      toast.error('Błąd podczas pobierania sugestii AI');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleGenerate = () => {
    if (!position || !companyName) {
      toast.info('Proszę wypełnić podstawowe dane');
      return;
    }

    const formData: CertificateFormData = {
      certificateType,
      position,
      companyName,
      startDate,
      endDate,
      responsibilities: responsibilities.filter(r => r.trim()),
      positiveTraits: positiveTraits.filter(t => t.trim()),
      skillsGained: skillsGained.filter(s => s.trim()),
      assessment,
    };

    onGenerate(formData);
  };

  const updateArrayItem = (arr: string[], index: number, value: string, setter: (arr: string[]) => void) => {
    const newArr = [...arr];
    newArr[index] = value;
    setter(newArr);
  };

  const addArrayItem = (arr: string[], setter: (arr: string[]) => void) => {
    setter([...arr, '']);
  };

  const removeArrayItem = (arr: string[], index: number, setter: (arr: string[]) => void) => {
    const newArr = arr.filter((_, i) => i !== index);
    setter(newArr);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Generator Zaświadczeń z AI</h2>
            <p className="text-indigo-100 text-sm mt-1">Wypełnij dane i użyj AI do wygenerowania treści</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Certificate Type Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Typ zaświadczenia</label>
              <div className="grid grid-cols-3 gap-3">
                {(['internship', 'volunteer', 'employment'] as const).map(type => (
                  <button
                    key={type}
                    onClick={() => setCertificateType(type)}
                    className={`p-3 rounded-lg border-2 transition ${
                      certificateType === type
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    <div className="font-medium text-sm">{certificateTypeLabels[type]}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Stanowisko/Rola *</label>
                <input
                  type="text"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="np. Asystent Marketingu"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {certificateType === 'volunteer' ? 'Organizacja' : 'Firma'} *
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="np. ABC Sp. z o.o."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data rozpoczęcia</label>
                <input
                  type="text"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="np. 01.01.2024"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data zakończenia</label>
                <input
                  type="text"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="np. 31.03.2024"
                />
              </div>
            </div>

            {/* AI Suggestions Button */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-2 border-purple-200">
              <button
                onClick={handleGetAISuggestions}
                disabled={loadingAI || !position || !companyName}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg transition"
              >
                {loadingAI ? (
                  <>
                    <Loader className="mr-2 animate-spin" size={20} />
                    Generowanie sugestii AI...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={20} />
                    {aiSuggestionsLoaded ? 'Wygeneruj ponownie sugestie AI' : 'Wygeneruj sugestie AI'}
                  </>
                )}
              </button>
              <p className="text-xs text-gray-600 mt-2 text-center">
                AI wygeneruje listę obowiązków, cech pozytywnych, umiejętności i ocenę
              </p>
            </div>

            {/* Responsibilities */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Zakres obowiązków</label>
              {responsibilities.map((resp, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={resp}
                    onChange={(e) => updateArrayItem(responsibilities, index, e.target.value, setResponsibilities)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder={`Obowiązek ${index + 1}`}
                  />
                  {responsibilities.length > 1 && (
                    <button
                      onClick={() => removeArrayItem(responsibilities, index, setResponsibilities)}
                      className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem(responsibilities, setResponsibilities)}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                + Dodaj obowiązek
              </button>
            </div>

            {/* Positive Traits */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cechy pozytywne</label>
              {positiveTraits.map((trait, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={trait}
                    onChange={(e) => updateArrayItem(positiveTraits, index, e.target.value, setPositiveTraits)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder={`Cecha ${index + 1}`}
                  />
                  {positiveTraits.length > 1 && (
                    <button
                      onClick={() => removeArrayItem(positiveTraits, index, setPositiveTraits)}
                      className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem(positiveTraits, setPositiveTraits)}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                + Dodaj cechę
              </button>
            </div>

            {/* Skills Gained */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Zdobyte umiejętności</label>
              {skillsGained.map((skill, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={skill}
                    onChange={(e) => updateArrayItem(skillsGained, index, e.target.value, setSkillsGained)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder={`Umiejętność ${index + 1}`}
                  />
                  {skillsGained.length > 1 && (
                    <button
                      onClick={() => removeArrayItem(skillsGained, index, setSkillsGained)}
                      className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem(skillsGained, setSkillsGained)}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                + Dodaj umiejętność
              </button>
            </div>

            {/* Assessment */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Ocena końcowa (2-3 zdania)</label>
              <textarea
                value={assessment}
                onChange={(e) => setAssessment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Krótka ocena wydajności i zaangażowania..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            Anuluj
          </button>
          <button
            onClick={handleGenerate}
            disabled={!position || !companyName}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition"
          >
            Generuj zaświadczenie
          </button>
        </div>
      </div>
    </div>
  );
};
